import { FC } from 'react';
import { useHistory } from 'react-router';

import invoiceImg from 'assets/brand/invoice.png';
import Button from 'components/_core/Button';
import SunshineModal from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';

import * as routes from '../../../../../../routes';
import { useConvertQuoteToInvoice } from '../../../mutations/useConvertQuoteToInvoice';
import { Quote } from '../../../queries/useGetQuote';
import { useQuoteModals } from '../..';
import locales from './locales';

const defaultInvoiceToGenerateFromQuote = {
  allowCreditCardPayment: true,
  // The user will be able to edit those fields in the invoice edit form
  // after the conversion
  allowIbanPayment: true,
};

export interface ConvertQuoteToInvoiceModalProps
  extends Pick<Quote, 'clientContactIds' | 'isUserCcedOnEmails' | 'quoteId'> {
  isFromList: boolean;
}

const ConvertQuoteToInvoiceModal: FC<ConvertQuoteToInvoiceModalProps> = ({
  clientContactIds,
  isFromList,
  isUserCcedOnEmails,
  quoteId,
}) => {
  const { hideModal } = useQuoteModals();
  const { push, replace } = useHistory();
  const { showToast } = useToast();

  const input = {
    ...defaultInvoiceToGenerateFromQuote,
    clientContactIds,
    isUserCcedOnEmails,
    quoteId,
  };

  const [convertQuoteToInvoice, { loading }] = useConvertQuoteToInvoice(input);

  const onConfirm = async () => {
    try {
      const { data } = await convertQuoteToInvoice();
      logEvent({
        name: 'Quote Converted To Invoice',
        properties: {
          from: isFromList ? 'QuoteListPage' : 'QuoteDetailsPage',
          quoteId,
        },
      });
      const invoiceId = data?.convertQuoteToInvoice?.invoice?.invoiceId;

      if (invoiceId) {
        // Because we want to go back to the quote list after editing the generated invoice
        const navigationMethod = isFromList ? push : replace;
        navigationMethod(routes.getEditInvoice(invoiceId));
      }
    } catch (error) {
      showToast({ message: locales.errorMessage, type: 'error' });
    }
  };

  return (
    <SunshineModal isOpen onClose={hideModal}>
      <SunshineModal.Header />

      <SunshineModal.Body>
        <SunshineModal.Body.Image src={invoiceImg} />
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>
        <Text>{locales.description}</Text>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          isDisabled={loading}
          onClick={hideModal}
          variant="inline-secondary"
        >
          {locales.cancel}
        </Button>

        <Button isLoading={loading} onClick={onConfirm}>
          {locales.confirm}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default ConvertQuoteToInvoiceModal;
